// Copyright 2026, Auxilio Technologies LLC

import React, { useState, useEffect } from 'react';
import {
  UsersIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  EnvelopeIcon,
  XMarkIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import { useAuthStore } from '../store/authStore';
import { authAPI } from '../services/api';
import {
  Roles,
  RoleType,
  RoleInfoMap,
  getAssignableRoles,
  isAdminRole,
  hasPermission,
  Permissions,
} from '../config/rolesConfig';
import { mockSites } from '../data/sitesData';

// User interface matching backend structure
interface ManagedUser {
  id: string;
  email: string;
  username: string;
  role: RoleType;
  status: 'active' | 'pending' | 'suspended';
  division?: string;
  siteId?: string;
  createdAt: string;
  lastLogin?: string;
}

// Site interface
interface Site {
  id: string;
  name: string;
  site_code: string;
}

const UserManagement: React.FC = () => {
  const { user: currentUser } = useAuthStore();
  const [users, setUsers] = useState<ManagedUser[]>([]);
  const [sites, setSites] = useState<Site[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedUser, setSelectedUser] = useState<ManagedUser | null>(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<ManagedUser | null>(null);
  const [inviteForm, setInviteForm] = useState({
    email: '',
    role: 'viewer' as RoleType,
    division: '',
    siteId: '',
  });
  const [editForm, setEditForm] = useState<Partial<ManagedUser>>({});
  const [inviteStatus, setInviteStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [deleteStatus, setDeleteStatus] = useState<'idle' | 'deleting' | 'error'>('idle');
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [fetchError, setFetchError] = useState<string | null>(null);

  // Get current user's role and determine what roles they can assign
  const currentUserRole = (currentUser?.role || 'viewer') as RoleType;
  const assignableRoles = getAssignableRoles(currentUserRole);
  const canManageUsers = hasPermission(currentUserRole, Permissions.USERS_WRITE);
  const canInviteUsers = hasPermission(currentUserRole, Permissions.USERS_INVITE);
  const canDeleteUsers = hasPermission(currentUserRole, Permissions.USERS_DELETE);

  // Fetch users and sites from API on component mount
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        console.log('[UserManagement] Fetching users via authenticated API');

        const response = await authAPI.listUsers();
        const data = response.data;
        console.log('[UserManagement] Users data:', data);

        // Map API response to ManagedUser format
        const mappedUsers: ManagedUser[] = data.users.map((u: any) => ({
          id: u.id,
          email: u.email,
          username: u.username || u.email.split('@')[0],
          role: u.role || 'viewer',
          status: u.email_verified ? 'active' : 'pending',
          division: u.division,
          siteId: u.site_id,
          createdAt: u.created_at,
          lastLogin: u.last_login,
        }));
        console.log('[UserManagement] Mapped users:', mappedUsers);
        setUsers(mappedUsers);
        setFetchError(null);
      } catch (error: any) {
        console.error('[UserManagement] Error fetching users:', error);
        const errorDetail = error.response?.data?.detail || error.message || 'Unknown error';
        setFetchError(`Failed to fetch users: ${errorDetail}`);
      } finally {
        setLoading(false);
      }
    };

    // Load sites from mock data (same data used by Sites page)
    const loadSites = () => {
      console.log('[UserManagement] Loading sites from mockSites, count:', mockSites.length);
      const mappedSites: Site[] = mockSites.map((s) => ({
        id: s.id,
        name: s.name,
        site_code: s.code,
      }));
      console.log('[UserManagement] Mapped sites:', mappedSites);
      setSites(mappedSites);
    };

    fetchUsers();
    loadSites();
  }, []);

  const getStatusColor = (status: ManagedUser['status']) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
      case 'suspended':
        return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400';
    }
  };

  const getStatusIcon = (status: ManagedUser['status']) => {
    switch (status) {
      case 'active':
        return <CheckCircleIcon className="w-4 h-4" />;
      case 'pending':
        return <ClockIcon className="w-4 h-4" />;
      case 'suspended':
        return <ExclamationTriangleIcon className="w-4 h-4" />;
      default:
        return null;
    }
  };

  const getRoleColor = (role: RoleType) => {
    const roleInfo = RoleInfoMap[role];
    if (!roleInfo) return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400';

    const colorMap: Record<string, string> = {
      purple: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
      red: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
      blue: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
      green: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
      teal: 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-400',
      indigo: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400',
      gray: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400',
    };
    return colorMap[roleInfo.color] || colorMap.gray;
  };

  const filteredUsers = users.filter((user) => {
    const matchesSearch =
      user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.username.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesRole = roleFilter === 'all' || user.role === roleFilter;
    const matchesStatus = statusFilter === 'all' || user.status === statusFilter;
    return matchesSearch && matchesRole && matchesStatus;
  });

  const handleInviteUser = async () => {
    if (!inviteForm.email || !inviteForm.role) return;

    setInviteStatus('sending');

    try {
      const response = await authAPI.inviteUser({
        email: inviteForm.email,
        role: inviteForm.role,
        division: inviteForm.division || null,
        site_id: inviteForm.siteId || null,
      });

      const data = response.data;

      // Create new pending user in local state
      const newUser: ManagedUser = {
        id: String(users.length + 1),
        email: inviteForm.email,
        username: inviteForm.email.split('@')[0],
        role: inviteForm.role,
        status: 'pending',
        division: inviteForm.division || undefined,
        siteId: inviteForm.siteId || undefined,
        createdAt: new Date().toISOString().split('T')[0],
      };

      setUsers([...users, newUser]);
      setInviteStatus('success');

      // Reset after showing success
      setTimeout(() => {
        setShowInviteModal(false);
        setInviteStatus('idle');
        setInviteForm({ email: '', role: 'viewer', division: '', siteId: '' });
      }, 1500);
    } catch (error) {
      console.error('Error inviting user:', error);
      setInviteStatus('error');
      // Reset after showing error briefly
      setTimeout(() => {
        setInviteStatus('idle');
      }, 3000);
    }
  };

  const handleEditUser = async () => {
    if (!selectedUser || !editForm.role) return;

    try {
      // Transform editForm to match backend API expectations
      const updateData: { role?: string; division?: string; site_id?: string; is_active?: boolean } = {};

      if (editForm.role && editForm.role !== selectedUser.role) {
        updateData.role = editForm.role;
      }
      if (editForm.division !== undefined && editForm.division !== selectedUser.division) {
        updateData.division = editForm.division;
      }
      if (editForm.siteId !== undefined && editForm.siteId !== selectedUser.siteId) {
        updateData.site_id = editForm.siteId;
      }
      if (editForm.status !== undefined && editForm.status !== selectedUser.status) {
        // Convert status to is_active boolean
        updateData.is_active = editForm.status === 'active';
      }

      // Call API to update user
      await authAPI.updateUser(selectedUser.id, updateData);

      // Update local state on success
      setUsers(
        users.map((u) =>
          u.id === selectedUser.id ? { ...u, ...editForm } : u
        )
      );
      setShowEditModal(false);
      setSelectedUser(null);
      setEditForm({});
    } catch (error: any) {
      console.error('Error updating user:', error);
      const errorDetail = error.response?.data?.detail || error.message || 'Failed to update user';
      alert(`Error: ${errorDetail}`);
    }
  };

  const handleDeleteUser = async (user: ManagedUser) => {
    setDeleteStatus('deleting');
    setDeleteError(null);

    try {
      await authAPI.deleteUser(user.id);
      // Remove user from local state
      setUsers(users.filter((u) => u.id !== user.id));
      setShowDeleteConfirm(null);
      setDeleteStatus('idle');
    } catch (error: any) {
      console.error('Error deleting user:', error);
      const errorDetail = error.response?.data?.detail || error.message || 'Failed to delete user';
      setDeleteError(errorDetail);
      setDeleteStatus('error');
    }
  };

  const openEditModal = (user: ManagedUser) => {
    setSelectedUser(user);
    setEditForm({
      role: user.role,
      division: user.division,
      siteId: user.siteId,
      status: user.status,
    });
    setShowEditModal(true);
  };

  // Summary stats
  const activeUsers = users.filter((u) => u.status === 'active').length;
  const pendingUsers = users.filter((u) => u.status === 'pending').length;
  const adminUsers = users.filter((u) => isAdminRole(u.role)).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">User Management</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            Manage user accounts, roles, and permissions
          </p>
        </div>
        {canInviteUsers && (
          <button
            onClick={() => setShowInviteModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
          >
            <PlusIcon className="w-5 h-5" />
            Invite User
          </button>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-teal-100 dark:bg-teal-900/30 rounded-lg">
              <UsersIcon className="w-6 h-6 text-teal-600 dark:text-teal-400" />
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Total Users</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{users.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
              <CheckCircleIcon className="w-6 h-6 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Active Users</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{activeUsers}</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg">
              <ClockIcon className="w-6 h-6 text-yellow-600 dark:text-yellow-400" />
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Pending Invites</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{pendingUsers}</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
              <ShieldCheckIcon className="w-6 h-6 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Administrators</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{adminUsers}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-4">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search users by email or username..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <FunnelIcon className="w-5 h-5 text-gray-400" />
              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-teal-500"
              >
                <option value="all">All Roles</option>
                {Object.values(Roles).map((role) => (
                  <option key={role} value={role}>
                    {RoleInfoMap[role]?.name || role}
                  </option>
                ))}
              </select>
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-teal-500"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="pending">Pending</option>
              <option value="suspended">Suspended</option>
            </select>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                User
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Role
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Division/Site
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Last Login
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {filteredUsers.map((user) => (
              <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-teal-100 dark:bg-teal-900/30 rounded-full flex items-center justify-center">
                      <span className="text-sm font-medium text-teal-600 dark:text-teal-400">
                        {user.username.substring(0, 2).toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">{user.username}</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">{user.email}</p>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleColor(user.role)}`}>
                    {RoleInfoMap[user.role]?.name || user.role}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(user.status)}`}>
                    {getStatusIcon(user.status)}
                    {user.status.charAt(0).toUpperCase() + user.status.slice(1)}
                  </span>
                </td>
                <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">
                  {(() => {
                    const siteName = user.siteId ? sites.find(s => s.id === user.siteId)?.name : null;
                    if (user.division && siteName) {
                      return `${user.division} / ${siteName}`;
                    } else if (user.division) {
                      return user.division;
                    } else if (siteName) {
                      return siteName;
                    }
                    return '-';
                  })()}
                </td>
                <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">
                  {user.lastLogin ? new Date(user.lastLogin).toLocaleDateString() : 'Never'}
                </td>
                <td className="px-6 py-4 text-right">
                  <div className="flex items-center justify-end gap-2">
                    {canManageUsers && (
                      <button
                        onClick={() => openEditModal(user)}
                        className="p-1.5 text-gray-400 hover:text-teal-500 hover:bg-teal-50 dark:hover:bg-teal-900/20 rounded transition-colors"
                        title="Edit user"
                      >
                        <PencilIcon className="w-4 h-4" />
                      </button>
                    )}
                    {canDeleteUsers && user.id !== currentUser?.id && (
                      <button
                        onClick={() => setShowDeleteConfirm(user)}
                        className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                        title="Delete user"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {loading ? (
          <div className="text-center py-12">
            <svg className="animate-spin h-8 w-8 text-teal-500 mx-auto mb-4" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            <p className="text-gray-500 dark:text-gray-400">Loading users...</p>
          </div>
        ) : fetchError ? (
          <div className="text-center py-12">
            <ExclamationTriangleIcon className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <p className="text-red-500 dark:text-red-400">{fetchError}</p>
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="text-center py-12">
            <UsersIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500 dark:text-gray-400">No users found matching your criteria</p>
          </div>
        ) : null}
      </div>

      {/* Invite User Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-teal-100 dark:bg-teal-900/30 rounded-lg">
                  <EnvelopeIcon className="w-6 h-6 text-teal-600 dark:text-teal-400" />
                </div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">Invite User</h2>
              </div>
              <button
                onClick={() => {
                  setShowInviteModal(false);
                  setInviteForm({ email: '', role: 'viewer', division: '', siteId: '' });
                  setInviteStatus('idle');
                }}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
              >
                <XMarkIcon className="w-6 h-6 text-gray-500" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-4">
              {inviteStatus === 'success' ? (
                <div className="text-center py-8">
                  <CheckCircleIcon className="w-16 h-16 text-green-500 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    Invitation Sent!
                  </h3>
                  <p className="text-gray-500 dark:text-gray-400">
                    An invitation email has been sent to {inviteForm.email}
                  </p>
                </div>
              ) : inviteStatus === 'error' ? (
                <div className="text-center py-8">
                  <ExclamationTriangleIcon className="w-16 h-16 text-red-500 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    Invitation Failed
                  </h3>
                  <p className="text-gray-500 dark:text-gray-400">
                    Failed to send invitation. The user may already exist or there was an email delivery issue.
                  </p>
                  <button
                    onClick={() => setInviteStatus('idle')}
                    className="mt-4 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
                  >
                    Try Again
                  </button>
                </div>
              ) : (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      value={inviteForm.email}
                      onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                      placeholder="user@company.com"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Role *
                    </label>
                    <select
                      value={inviteForm.role}
                      onChange={(e) => setInviteForm({ ...inviteForm, role: e.target.value as RoleType })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500"
                    >
                      {assignableRoles.map((role) => (
                        <option key={role} value={role}>
                          {RoleInfoMap[role]?.name || role} - {RoleInfoMap[role]?.description}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      You can only assign roles below your permission level
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Division (Optional)
                    </label>
                    <input
                      type="text"
                      value={inviteForm.division}
                      onChange={(e) => setInviteForm({ ...inviteForm, division: e.target.value })}
                      placeholder="e.g., Logistics, Operations"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Site (Optional)
                    </label>
                    <select
                      value={inviteForm.siteId}
                      onChange={(e) => setInviteForm({ ...inviteForm, siteId: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500"
                    >
                      <option value="">All Sites</option>
                      {sites.map((site) => (
                        <option key={site.id} value={site.id}>
                          {site.name} ({site.site_code})
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
                      What happens next?
                    </h4>
                    <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                      <li>1. User receives an email invitation</li>
                      <li>2. User clicks the link to create their account</li>
                      <li>3. User completes registration and email verification</li>
                      <li>4. Account becomes active with assigned role</li>
                    </ul>
                  </div>
                </>
              )}
            </div>

            {/* Modal Footer */}
            {inviteStatus !== 'success' && (
              <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={() => {
                    setShowInviteModal(false);
                    setInviteForm({ email: '', role: 'viewer', division: '', siteId: '' });
                  }}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleInviteUser}
                  disabled={!inviteForm.email || inviteStatus === 'sending'}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {inviteStatus === 'sending' ? (
                    <>
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Sending...
                    </>
                  ) : (
                    <>
                      <EnvelopeIcon className="w-4 h-4" />
                      Send Invitation
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-teal-100 dark:bg-teal-900/30 rounded-lg">
                  <PencilIcon className="w-6 h-6 text-teal-600 dark:text-teal-400" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white">Edit User</h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{selectedUser.email}</p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setSelectedUser(null);
                  setEditForm({});
                }}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
              >
                <XMarkIcon className="w-6 h-6 text-gray-500" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Role
                </label>
                <select
                  value={editForm.role || selectedUser.role}
                  onChange={(e) => setEditForm({ ...editForm, role: e.target.value as RoleType })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500"
                >
                  {/* Include current role even if not assignable */}
                  {!assignableRoles.includes(selectedUser.role) && (
                    <option value={selectedUser.role}>
                      {RoleInfoMap[selectedUser.role]?.name || selectedUser.role} (current)
                    </option>
                  )}
                  {assignableRoles.map((role) => (
                    <option key={role} value={role}>
                      {RoleInfoMap[role]?.name || role}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Status
                </label>
                <select
                  value={editForm.status || selectedUser.status}
                  onChange={(e) => setEditForm({ ...editForm, status: e.target.value as ManagedUser['status'] })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500"
                >
                  <option value="active">Active</option>
                  <option value="suspended">Suspended</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Division
                </label>
                <input
                  type="text"
                  value={editForm.division ?? selectedUser.division ?? ''}
                  onChange={(e) => setEditForm({ ...editForm, division: e.target.value })}
                  placeholder="e.g., Logistics, Operations"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Site
                </label>
                <select
                  value={editForm.siteId ?? selectedUser.siteId ?? ''}
                  onChange={(e) => setEditForm({ ...editForm, siteId: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500"
                >
                  <option value="">All Sites</option>
                  {sites.map((site) => (
                    <option key={site.id} value={site.id}>
                      {site.name} ({site.site_code})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setSelectedUser(null);
                  setEditForm({});
                }}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleEditUser}
                className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="p-3 bg-red-100 dark:bg-red-900/30 rounded-full">
                  <TrashIcon className="w-6 h-6 text-red-600 dark:text-red-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Delete User</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">This action cannot be undone</p>
                </div>
              </div>
              <p className="text-gray-600 dark:text-gray-300 mb-4">
                Are you sure you want to delete <span className="font-semibold">{showDeleteConfirm.username}</span> ({showDeleteConfirm.email})?
              </p>
              {deleteError && (
                <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                  <p className="text-sm text-red-600 dark:text-red-400">{deleteError}</p>
                </div>
              )}
              <div className="flex items-center justify-end gap-3">
                <button
                  onClick={() => {
                    setShowDeleteConfirm(null);
                    setDeleteError(null);
                    setDeleteStatus('idle');
                  }}
                  disabled={deleteStatus === 'deleting'}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDeleteUser(showDeleteConfirm)}
                  disabled={deleteStatus === 'deleting'}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {deleteStatus === 'deleting' ? (
                    <>
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Deleting...
                    </>
                  ) : (
                    'Delete User'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
